import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getIncomingOrders, updateOrderStatus, requestReview, shipOrder, getSellerOffers, respondToOffer, remindBuyer } from '../services/api';
import { OrderDetailModal } from '../components/OrderDetailModal';
import { Search, Download, Eye, Check, Truck, ClipboardList, Star, Loader2, Package, MessageSquare, Clock, X, Send, Bell } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────
interface Offer {
  id: string;
  listing_title: string;
  listing_id: string;
  buyer_username: string;
  buyer_offer_amount: number;
  buyer_offer_at: string;
  seller_response: null | 'accept' | 'reject' | 'counter';
  seller_counter_amount: null | number;
  buyer_final_response: null | 'accept' | 'reject';
  agreed_price: null | number;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  expires_at: string;
  listing_price?: number;
}

// ── Countdown Hook ───────────────────────────────────────────────────────────
function useCountdown(expiresAt: string) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('Expired');
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft(`${hours}h ${minutes}m remaining`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  return timeLeft;
}

// ── Offer Card Component ─────────────────────────────────────────────────────
const OfferCard: React.FC<{
  offer: Offer;
  onRespond: (offerId: string, response: 'accept' | 'reject' | 'counter', counterAmount?: number) => Promise<void>;
}> = ({ offer, onRespond }) => {
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterError, setCounterError] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const countdown = useCountdown(offer.expires_at);
  const isExpired = offer.status === 'expired' || countdown === 'Expired';

  const handleRespond = async (response: 'accept' | 'reject' | 'counter', amount?: number) => {
    setIsResponding(true);
    try {
      await onRespond(offer.id, response, amount);
    } finally {
      setIsResponding(false);
    }
  };

  const handleCounter = () => {
    const amount = parseFloat(counterAmount);
    if (isNaN(amount) || amount <= offer.buyer_offer_amount) {
      setCounterError(`Counter must be greater than LKR ${offer.buyer_offer_amount.toLocaleString()}`);
      return;
    }
    setCounterError('');
    handleCounter_submit(amount);
  };

  const handleCounter_submit = async (amount: number) => {
    await handleRespond('counter', amount);
    setCounterOpen(false);
    setCounterAmount('');
  };

  // ── Already responded states ───────────────────────────────────────────────
  const renderResponseState = () => {
    if (isExpired && !offer.seller_response) {
      return (
        <div className="mt-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-500 text-sm font-medium">
          ⏰ Offer expired
        </div>
      );
    }

    if (offer.seller_response === 'accept') {
      return (
        <div className="mt-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-medium">
          ✅ You accepted LKR {offer.buyer_offer_amount.toLocaleString()} — Order created
        </div>
      );
    }

    if (offer.seller_response === 'reject') {
      return (
        <div className="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium">
          ❌ You rejected this offer
        </div>
      );
    }

    if (offer.seller_response === 'counter') {
      if (offer.buyer_final_response === null) {
        return (
          <div className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm font-medium">
            <div className="flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Waiting for buyer response...
            </div>
            <div className="mt-1 text-amber-600">Your counter: LKR {offer.seller_counter_amount?.toLocaleString()}</div>
          </div>
        );
      }
      if (offer.buyer_final_response === 'accept') {
        return (
          <div className="mt-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-medium">
            ✅ Buyer accepted LKR {offer.seller_counter_amount?.toLocaleString()}
          </div>
        );
      }
      if (offer.buyer_final_response === 'reject') {
        return (
          <div className="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-medium">
            ❌ Buyer rejected your counter
          </div>
        );
      }
    }

    return null;
  };

  const hasResponded = offer.seller_response !== null;
  const showActions = !hasResponded && !isExpired;

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
      isExpired ? 'border-gray-200 opacity-70' : 'border-gray-100'
    }`}>
      <div className="p-5">
        {/* Title & Buyer */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
              📚 {offer.listing_title}
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Buyer: <span className="font-semibold text-gray-700">@{offer.buyer_username}</span>
            </p>
          </div>
          {/* Status pill */}
          {offer.status === 'accepted' && (
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold whitespace-nowrap">✅ Accepted</span>
          )}
          {offer.status === 'rejected' && (
            <span className="px-3 py-1 bg-red-100 text-red-600 rounded-full text-xs font-semibold whitespace-nowrap">❌ Rejected</span>
          )}
          {offer.status === 'expired' && (
            <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-semibold whitespace-nowrap">⏰ Expired</span>
          )}
          {offer.status === 'pending' && !isExpired && (
            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold whitespace-nowrap">⏳ Pending</span>
          )}
        </div>

        {/* Price & Expiry */}
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
          <div>
            <span className="text-2xl font-bold text-[#085041]">LKR {offer.buyer_offer_amount.toLocaleString()}</span>
            {offer.listing_price && (
              <span className="ml-2 text-sm text-gray-400 line-through">LKR {offer.listing_price.toLocaleString()}</span>
            )}
          </div>
          {!isExpired && offer.status === 'pending' && (
            <div className="flex items-center gap-1.5 text-sm text-amber-600 font-medium">
              <Clock size={14} />
              {countdown} ⏰
            </div>
          )}
        </div>

        {/* Response state */}
        {renderResponseState()}

        {/* Action buttons */}
        {showActions && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              disabled={isResponding}
              onClick={() => handleRespond('accept')}
              className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
            >
              {isResponding ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Accept
            </button>
            <button
              disabled={isResponding}
              onClick={() => handleRespond('reject')}
              className="flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-xl font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
            >
              <X size={16} />
              Reject
            </button>
            <button
              disabled={isResponding}
              onClick={() => setCounterOpen(!counterOpen)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-teal-50 text-[#085041] border border-teal-200 rounded-xl font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
            >
              <MessageSquare size={16} />
              Counter Offer
            </button>
          </div>
        )}

        {/* Counter input */}
        {counterOpen && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl animate-fade-in">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Counter Amount (LKR)</label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={counterAmount}
                onChange={(e) => { setCounterAmount(e.target.value); setCounterError(''); }}
                placeholder={`Must be > ${offer.buyer_offer_amount.toLocaleString()}`}
                className="flex-1 bg-white border border-gray-300 rounded-xl py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all text-sm"
              />
              <button
                disabled={isResponding}
                onClick={handleCounter}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#085041] hover:bg-[#063b30] text-white rounded-xl font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
              >
                <Send size={14} />
                Send Counter
              </button>
            </div>
            {counterError && (
              <p className="mt-2 text-red-500 text-xs font-medium">{counterError}</p>
            )}
            <p className="mt-3 text-xs text-gray-400 leading-relaxed">
              ℹ️ You can only counter once. If the buyer rejects, the offer closes — no further rounds.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Shipping Form Component ──────────────────────────────────────────────────
const ShippingForm: React.FC<{
  orderId: string;
  onSubmit: (orderId: string, trackingNote: string) => Promise<void>;
  onCancel: () => void;
}> = ({ orderId, onSubmit, onCancel }) => {
  const [trackingNote, setTrackingNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(orderId, trackingNote);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <tr className="bg-teal-50/40 animate-fade-in">
      <td colSpan={9} className="p-4 pl-16">
        <div className="max-w-lg">
          <h4 className="font-bold text-[#085041] text-sm mb-3 flex items-center gap-2">
            <Package size={16} />
            Confirm Shipment
          </h4>
          <textarea
            value={trackingNote}
            onChange={(e) => setTrackingNote(e.target.value.slice(0, 300))}
            maxLength={300}
            rows={2}
            placeholder="e.g. Sent via Lanka Post — tracking LE123456789LK"
            className="w-full bg-white border border-teal-200 rounded-xl py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all text-sm resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400">{trackingNote.length}/300</span>
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={handleSubmit}
                className="flex items-center gap-2 px-5 py-2 bg-[#085041] hover:bg-[#063b30] text-white rounded-xl font-semibold text-sm transition-colors shadow-sm disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Confirm Shipment
              </button>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// ── Status Tabs ──────────────────────────────────────────────────────────────
const ORDER_FILTER_TABS = ['All', 'Pending', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'];

// ── Main Component ───────────────────────────────────────────────────────────
const Orders: React.FC = () => {
  // ── Existing state (unchanged) ─────────────────────────────────────────────
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  
  const [toast, setToast] = useState<{ msg: string, visible: boolean, type?: 'success' | 'error' }>({ msg: '', visible: false });

  // ── New state: page-level tab ──────────────────────────────────────────────
  const [pageTab, setPageTab] = useState<'orders' | 'offers'>('orders');

  // ── New state: shipping form ───────────────────────────────────────────────
  const [shippingOrderId, setShippingOrderId] = useState<string | null>(null);

  // ── New state: offers ──────────────────────────────────────────────────────
  const [offers, setOffers] = useState<Offer[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // ── New state: remind buyer (FIX 30, Aug 15, 2026) ─────────────────────────
  const [remindingId, setRemindingId] = useState<string | null>(null);

  // ── Existing fetch (unchanged) ─────────────────────────────────────────────
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await getIncomingOrders();
      // Inject mock data for missing fields
      const extendedData = data.map((o: any) => ({
        ...o,
        buyer_city: "Colombo", // Mock
        condition: "New", // Mock
        cover: null, // Mock
        listing_id: null, // Mock
      }));
      setOrders(extendedData);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ── Fetch offers when tab switches ─────────────────────────────────────────
  const fetchOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const data = await getSellerOffers();
      setOffers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch offers:", err);
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pageTab === 'offers') {
      fetchOffers();
    }
  }, [pageTab, fetchOffers]);

  // ── Toast (existing, preserved) ────────────────────────────────────────────
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, visible: true, type });
    setTimeout(() => setToast({ msg: '', visible: false }), 3000);
  };

  // ── Filters (existing, preserved) ──────────────────────────────────────────
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (activeTab !== 'All') {
      result = result.filter(o => o.status.toLowerCase() === activeTab.toLowerCase());
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o => 
        o.buyer_name?.toLowerCase().includes(q) || 
        o.listing_title?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [orders, activeTab, searchQuery]);

  // ── Selection (existing, preserved) ────────────────────────────────────────
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredOrders.map(o => o.order_id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ── Status update (existing, preserved) ────────────────────────────────────
  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await updateOrderStatus(id, status);
      showToast(
        status === 'confirmed' ? '✅ Order confirmed!' :
        status === 'shipped' ? '✅ Marked as shipped!' :
        status === 'delivered' ? '✅ Order delivered!' :
        '✅ Order cancelled!'
      );
      fetchOrders();
      if (selectedOrder?.order_id === id) {
        setSelectedOrder((prev: any) => ({ ...prev, status }));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  // ── NEW: Ship with tracking note ───────────────────────────────────────────
  const handleShipOrder = async (orderId: string, trackingNote: string) => {
    try {
      await shipOrder(orderId, trackingNote);
      showToast('Shipment confirmed ✅ Buyer has been notified');
      setShippingOrderId(null);
      fetchOrders();
    } catch (err) {
      console.error(err);
      showToast('Failed to confirm shipment', 'error');
    }
  };

  // ── NEW: Remind Buyer (FIX 30, Aug 15, 2026) ───────────────────────────────
  // Sends the buyer a "please confirm your order" notification only - never
  // changes order status. Backend enforces a 24h cooldown per order (429 if
  // still in cooldown), shown here as a toast rather than a silent failure.
  const handleRemindBuyer = async (orderId: string) => {
    setRemindingId(orderId);
    try {
      await remindBuyer(orderId);
      showToast('🔔 Reminder sent to buyer');
    } catch (err: any) {
      if (err?.response?.status === 429) {
        showToast('A reminder was already sent recently - please wait before sending another', 'error');
      } else {
        showToast('Failed to send reminder', 'error');
      }
    } finally {
      setRemindingId(null);
    }
  };

  // ── NEW: Respond to offer ──────────────────────────────────────────────────
  const handleOfferRespond = async (offerId: string, response: 'accept' | 'reject' | 'counter', counterAmount?: number) => {
    try {
      await respondToOffer(offerId, response, counterAmount);
      const messages: Record<string, string> = {
        accept: '✅ Offer accepted — Order auto-created',
        reject: '❌ Offer rejected',
        counter: '💬 Counter offer sent — Waiting for buyer',
      };
      showToast(messages[response] || '✅ Done');
      fetchOffers();
    } catch (err) {
      console.error(err);
      showToast('Failed to respond to offer', 'error');
    }
  };

  // ── Bulk update (existing, preserved) ──────────────────────────────────────
  const handleBulkUpdate = async (status: string) => {
    if (selectedIds.length === 0) return;
    setIsUpdating(true);
    showToast(`Updating ${selectedIds.length} orders...`);
    try {
      await Promise.all(selectedIds.map(id => updateOrderStatus(id, status)));
      showToast(`✅ All selected orders updated to ${status}!`);
      fetchOrders();
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      alert("Some orders failed to update.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ── CSV export (existing, preserved) ───────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Order ID', 'Book Title', 'Buyer Name', 'Buyer City', 'Price', 'Status', 'Date', 'Order Notes'];
    const rows = filteredOrders.map(o => {
      const note = localStorage.getItem(`order_note_${o.order_id}`) || '';
      return [
        o.order_id,
        `"${o.listing_title.replace(/"/g, '""')}"`,
        `"${o.buyer_name.replace(/"/g, '""')}"`,
        `"${o.buyer_city}"`,
        o.price_lkr,
        o.status,
        new Date(o.created_at).toLocaleDateString(),
        `"${note.replace(/"/g, '""')}"`
      ];
    });
    
    const csvData = [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `readora-orders-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('✅ CSV exported!');
  };

  // ── Status badge (UPDATED: shipped → orange, delivered → ✅ green) ─────────
  const getStatusBadge = (status: string) => {
    switch(status.toLowerCase()) {
      case 'pending': return <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold whitespace-nowrap">⏳ Pending</span>;
      case 'confirmed': return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold whitespace-nowrap">✅ Confirmed</span>;
      case 'shipped': return <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold whitespace-nowrap">🚚 Shipped</span>;
      case 'delivered': return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap">✅ Delivered</span>;
      case 'cancelled': return <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold whitespace-nowrap">❌ Cancelled</span>;
      default: return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold whitespace-nowrap">{status}</span>;
    }
  };

  // ── Helper: check if shipped > 7 days ago ──────────────────────────────────
  // FIX 30 (Aug 15, 2026): renamed from isAutoConfirmable - nothing auto-
  // confirms anymore (see routers/marketplace.py, auto_confirm_delivery's
  // removal). This now just flags "buyer is overdue to confirm," used below
  // to show an accurate note instead of the old false "Auto-confirmed by
  // system" claim.
  const isReminderEligible = (order: any) => {
    if (order.status !== 'shipped' || !order.shipped_at) return false;
    const diff = Date.now() - new Date(order.shipped_at).getTime();
    return diff > 7 * 24 * 60 * 60 * 1000;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen bg-[#F1EFE8]">
      
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl z-50 animate-fade-in-up font-medium ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* TOP BAR / HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-teal-900">Orders</h1>
          <p className="text-gray-500 font-medium mt-1">{orders.length} total orders</p>
        </div>
        {pageTab === 'orders' && (
          <button 
            onClick={exportCSV}
            className="flex items-center gap-2 bg-white text-teal-900 border border-teal-200 hover:bg-teal-50 py-2.5 px-5 rounded-xl font-semibold transition-colors shadow-sm"
          >
            <Download size={18} /> Export CSV
          </button>
        )}
      </div>

      {/* ═══ PAGE-LEVEL TABS: Orders | Offers ═══ */}
      <div className="bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 flex gap-1 mb-6 w-fit">
        <button
          onClick={() => setPageTab('orders')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
            pageTab === 'orders'
              ? 'bg-[#085041] text-white shadow-lg'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          }`}
        >
          <ClipboardList size={18} />
          📋 Orders
        </button>
        <button
          onClick={() => setPageTab('offers')}
          className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all ${
            pageTab === 'offers'
              ? 'bg-[#085041] text-white shadow-lg'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          }`}
        >
          <MessageSquare size={18} />
          💬 Offers
          {offers.filter(o => o.status === 'pending' && !o.seller_response).length > 0 && (
            <span className="ml-1 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {offers.filter(o => o.status === 'pending' && !o.seller_response).length}
            </span>
          )}
        </button>
      </div>

      {/* ═══ ORDERS TAB CONTENT ═══ */}
      {pageTab === 'orders' && (
        <>
          {/* FILTER TABS & SEARCH */}
          <div className="bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex flex-col xl:flex-row justify-between items-center gap-4 mb-6">
            <div className="flex flex-wrap gap-1 p-1 w-full xl:w-auto">
              {ORDER_FILTER_TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    activeTab === tab 
                      ? 'bg-[#085041] text-white shadow-md' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
            
            <div className="relative w-full xl:w-72 pr-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Search by buyer or book..." 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-[#085041]/20 focus:border-[#085041] transition-all"
              />
            </div>
          </div>

          {/* BULK ACTIONS (if selected) */}
          {selectedIds.length > 0 && (
            <div className="bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4 animate-fade-in">
              <div className="flex items-center gap-2 text-[#085041] font-semibold">
                <Check className="text-[#1D9E75]" size={20} />
                <span>{selectedIds.length} selected</span>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button 
                  disabled={isUpdating}
                  onClick={() => handleBulkUpdate('confirmed')}
                  className="flex items-center gap-1.5 bg-white text-[#085041] border border-[#085041]/20 hover:bg-[#085041]/5 px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm"
                >
                  <Check size={16} /> Confirm All
                </button>
                <button 
                  disabled={isUpdating}
                  onClick={() => handleBulkUpdate('shipped')}
                  className="flex items-center gap-1.5 bg-white text-[#1D9E75] border border-[#1D9E75]/20 hover:bg-[#1D9E75]/5 px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm"
                >
                  <Truck size={16} /> Ship All
                </button>
                <button 
                  disabled={isUpdating}
                  onClick={() => {
                    if (window.confirm("Are you sure you want to cancel these orders?")) {
                      handleBulkUpdate('cancelled');
                    }
                  }}
                  className="flex items-center gap-1.5 bg-white text-[#D85A30] border border-[#D85A30]/20 hover:bg-[#D85A30]/5 px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm"
                >
                  Cancel All
                </button>
              </div>
            </div>
          )}

          {/* ORDERS TABLE */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto w-full">
              {loading ? (
                <div className="p-12 flex flex-col items-center justify-center text-teal-800">
                   <Loader2 size={32} className="animate-spin text-teal-600 mb-4" />
                   <p className="font-medium text-gray-500">Loading orders...</p>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="p-12 flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ClipboardList size={32} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No orders found.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100 text-gray-500 text-sm uppercase tracking-wider font-semibold">
                      <th className="p-4 w-12 text-center">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 rounded text-[#085041] border-gray-300 focus:ring-[#085041]"
                          checked={selectedIds.length === filteredOrders.length && filteredOrders.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th className="p-4 py-5">Order ID</th>
                      <th className="p-4">Book</th>
                      <th className="p-4">Buyer</th>
                      <th className="p-4">Price</th>
                      <th className="p-4 text-center">Status</th>
                      <th className="p-4">Shipped At</th>
                      <th className="p-4 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredOrders.map((order) => (
                      <React.Fragment key={order.order_id}>
                        <tr className="hover:bg-teal-50/30 transition-colors group">
                          <td className="p-4 text-center">
                            <input 
                              type="checkbox" 
                              className="w-4 h-4 rounded text-[#085041] border-gray-300 focus:ring-[#085041]"
                              checked={selectedIds.includes(order.order_id)}
                              onChange={() => toggleSelect(order.order_id)}
                            />
                          </td>
                          <td className="p-4">
                            <span className="font-mono bg-[#085041]/10 text-[#085041] px-2 py-1 rounded text-sm font-semibold uppercase">
                              #{order.order_id.slice(0, 8)}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-[40px] h-[55px] bg-gray-200 rounded flex-shrink-0 shadow-sm overflow-hidden flex items-center justify-center">
                                {order.cover ? (
                                  <img src={order.cover} alt="cover" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="text-gray-400 text-xs text-center leading-tight">No<br/>Cover</div>
                                )}
                              </div>
                              <span className="font-bold text-gray-900 line-clamp-2 max-w-[150px]">
                                {order.listing_title}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-900">{order.buyer_name}</span>
                              <span className="text-sm text-gray-500">{order.buyer_city}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="font-bold text-gray-900">
                              Rs. {order.price_lkr.toLocaleString()}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            {getStatusBadge(order.status)}
                          </td>
                          {/* NEW: Shipped At column */}
                          <td className="p-4">
                            {order.shipped_at ? (
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-700 font-medium">
                                  {new Date(order.shipped_at).toLocaleDateString()}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {new Date(order.shipped_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isReminderEligible(order) && (
                                  <span className="text-xs text-amber-600 mt-1 italic">Buyer hasn't confirmed yet</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-300">—</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-center gap-2">
                              {order.status === 'delivered' ? (
                                <button 
                                  title="Request Review"
                                  onClick={async () => {
                                    try {
                                      const res = await requestReview(order.order_id);
                                      showToast(res?.message || '✅ Review request sent!');
                                    } catch(e) {
                                      showToast('Error sending review request', 'error');
                                    }
                                  }}
                                  className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors border border-amber-200 shadow-sm group-hover:shadow"
                                >
                                  <Star size={18} />
                                </button>
                              ) : order.status === 'pending' ? (
                                <button 
                                  title="Confirm"
                                  onClick={() => handleStatusUpdate(order.order_id, 'confirmed')}
                                  className="p-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg transition-colors border border-teal-200 shadow-sm group-hover:shadow"
                                >
                                  <Check size={18} />
                                </button>
                              ) : order.status === 'confirmed' ? (
                                <button 
                                  title="Mark as Shipped"
                                  onClick={() => setShippingOrderId(
                                    shippingOrderId === order.order_id ? null : order.order_id
                                  )}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-xs transition-all shadow-sm ${
                                    shippingOrderId === order.order_id
                                      ? 'bg-teal-700 text-white'
                                      : 'bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 group-hover:shadow'
                                  }`}
                                >
                                  <Package size={14} />
                                  📦 Mark as Shipped
                                </button>
                              ) : order.status === 'shipped' ? (
                                <button
                                  title="Remind Buyer to confirm"
                                  disabled={remindingId === order.order_id}
                                  onClick={() => handleRemindBuyer(order.order_id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-xs transition-all shadow-sm bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 group-hover:shadow disabled:opacity-50"
                                >
                                  {remindingId === order.order_id ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Bell size={14} />
                                  )}
                                  🔔 Remind Buyer
                                </button>
                              ) : null}

                              <button
                                title="View Details"
                                onClick={() => setSelectedOrder(order)}
                                className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg transition-colors border border-gray-200 shadow-sm group-hover:shadow"
                              >
                                <Eye size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Inline shipping form */}
                        {shippingOrderId === order.order_id && (
                          <ShippingForm
                            orderId={order.order_id}
                            onSubmit={handleShipOrder}
                            onCancel={() => setShippingOrderId(null)}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══ OFFERS TAB CONTENT ═══ */}
      {pageTab === 'offers' && (
        <div>
          {offersLoading ? (
            <div className="p-12 flex flex-col items-center justify-center text-teal-800 bg-white rounded-2xl shadow-sm border border-gray-100">
              <Loader2 size={32} className="animate-spin text-teal-600 mb-4" />
              <p className="font-medium text-gray-500">Loading offers...</p>
            </div>
          ) : offers.length === 0 ? (
            <div className="p-12 flex flex-col items-center justify-center text-center bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MessageSquare size={32} className="text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No offers yet.</p>
              <p className="text-gray-400 text-sm mt-1">When buyers make offers on your listings, they'll appear here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {offers.map(offer => (
                <OfferCard
                  key={offer.id}
                  offer={offer}
                  onRespond={handleOfferRespond}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <OrderDetailModal 
        isOpen={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        order={selectedOrder}
        onStatusUpdate={handleStatusUpdate}
        onRequestReview={async (id) => {
          try {
            const res = await requestReview(id);
            showToast(res?.message || '✅ Review request sent!');
          } catch(e) {
            showToast('Error sending review request', 'error');
          }
        }}
      />
    </div>
  );
};

export default Orders;
